import type { CrawlRun } from '@/hooks/useCrawlRuns';
import type { RoasterHealth } from '@/lib/crawlRuns';
import { relTime, shortError } from '@/lib/crawlRuns';

import { AlertTriangleIcon, RefreshIcon } from './crawlIcons';

interface CrawlAttentionSectionProps {
  alerting: RoasterHealth[];
  onRerun: (run: CrawlRun) => void;
}

// Surfaces roasters whose crawls have failed repeatedly (>= 3 in a row) with a
// one-tap re-run. Hidden entirely when nothing is alerting.
export function CrawlAttentionSection({ alerting, onRerun }: CrawlAttentionSectionProps) {
  if (alerting.length === 0) return null;
  return (
    <div className="mb-[18px] rounded-xl bg-wolisho-red/[0.06] p-4" data-testid="crawl-attention">
      <div className="mb-3 flex items-center gap-[7px]">
        <span className="flex text-wolisho-red">
          <AlertTriangleIcon size={16} />
        </span>
        <h2 className="m-0 text-sm font-bold text-wolisho-red">Needs attention</h2>
      </div>
      <div className="flex flex-col gap-2.5">
        {alerting.map((entry) => (
          <div key={entry.roasterId} className="flex items-start gap-2.5">
            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-bold text-black">{entry.roasterName}</p>
              <p className="mt-0.5 text-[12.5px] font-bold text-wolisho-red">
                Failed {entry.consecutiveFailures} crawls in a row
              </p>
              <p className="mt-[3px] truncate text-xs text-wolisho-brown/60">
                {shortError(entry.lastRun.error)} · last {relTime(entry.lastRun.startedAt)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRerun(entry.lastRun)}
              data-testid={`crawl-attention-rerun-${entry.roasterId}`}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border-0 bg-wolisho-red px-3 py-1.5 text-xs font-bold text-white"
            >
              <RefreshIcon size={12} /> Re-run
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
